import { useState, useMemo, useRef, useEffect } from "react";
import ModalPV from "./ModalPV";
const BASE = "https://support.gs-group.fr/api";
const h = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const BLINK_CSS = `
@keyframes pipeline-blink{0%,100%{opacity:1}50%{opacity:0.35}}
@keyframes pipeline-shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-3px)}40%,80%{transform:translateX(3px)}}
`;

// ── 5 colonnes identiques pour les 3 onglets ─────────────────────
const COLS_SAV = [
  {id:"ouvert",        real:"ouvert",        lbl:"Ouvert",         color:"#6366f1"},
  {id:"en_cours",      real:"en_cours",      lbl:"En cours",       color:"#f59e0b"},
  {id:"resolu",        real:"resolu",        lbl:"Résolu",         color:"#10b981"},
  {id:"non_resolu",    real:"non_resolu",    lbl:"Non résolu",     color:"#ef4444"},
  {id:"a_replanifier", real:"a_replanifier", lbl:"À re-planifier", color:"#f97316"},
];
const COLS_INT = [
  {id:"planifiee",     real:"planifiee",     lbl:"Planifiée",      color:"#6366f1"},
  {id:"en_cours",      real:"en_cours",      lbl:"En cours",       color:"#f59e0b"},
  {id:"terminee",      real:"terminee",      lbl:"Terminé",        color:"#10b981"},
  {id:"non_resolu",    real:"non_resolu",    lbl:"Non résolu",     color:"#ef4444"},
  {id:"a_replanifier", real:"a_replanifier", lbl:"À re-planifier", color:"#f97316"},
];

const getColSav = t => {
  const s = t.statut||"";
  if(["resolu","resolved","ferme","closed","completed"].includes(s)) return "resolu";
  if(s==="non_resolu") return "non_resolu";
  if(["a_replanifier","en_attente"].includes(s)) return "a_replanifier";
  if(["en_cours","in-progress"].includes(s)) return "en_cours";
  return "ouvert";
};
const getColInt = i => {
  const s = i.statut||"";
  if(["terminee","completed"].includes(s)) return "terminee";
  if(s==="non_resolu") return "non_resolu";
  if(["a_replanifier","annulee","cancelled"].includes(s)) return "a_replanifier";
  if(["en_cours","in-progress"].includes(s)) return "en_cours";
  return "planifiee";
};

const PRIO_HOT = ["critique","urgente","urgent","haute","high"];
const hoursAgo = d => d?(Date.now()-new Date(d).getTime())/3600000:0;
const fmtAge = d => { const hh=hoursAgo(d); return hh<1?"<1h":hh<24?Math.round(hh)+"h":Math.round(hh/24)+"j"; };
const isLate = (item, mode) => {
  if(["terminee","resolu","non_resolu","a_replanifier"].includes(item._col||"")) return false;
  if(mode==="sav") return hoursAgo(item.created_at)>48;
  const d = item.date_planifiee||item.date_intervention;
  return d && hoursAgo(d)>0;
};

export default function Pipeline({T, tickets=[], interventions=[], clients=[], techniciens=[], onUpdated, onSelectTicket, onSelectInt, onNewTicket, onNewInt, echecAlerts=[]}) {

  const deleteItem = async (item) => {
    if(!window.confirm("Supprimer cet élément ?")) return;
    const m = localStorage.getItem("pipeline_mode")||"sav";
    const url = m==="sav" ? `${BASE}/tickets/${item.id}` : `${BASE}/interventions/${item.id}`;
    await fetch(url, {method:"DELETE", headers:h()});
    onUpdated&&onUpdated();
  };

  const assignTech = async (item, techId) => {
    const m = localStorage.getItem("pipeline_mode")||"sav";
    if(m==="sav") {
      await fetch(`${BASE}/tickets/${item.id}`, {method:"PATCH", headers:h(), body:JSON.stringify({agent_id:techId})});
    } else {
      await fetch(`${BASE}/interventions/${item.id}/assigner`, {method:"PATCH", headers:h(), body:JSON.stringify({technicien_id:techId, commentaire:"Assigné depuis Pipeline"})});
    }
    onUpdated&&onUpdated();
  };

  const [mode, setMode]         = useState(()=>{
    const saved = localStorage.getItem("pipeline_mode")||"sav";
    // anciens modes fusionnés → int
    return (saved==="terrain"||saved==="install") ? "int" : saved;
  });
  const [drag, setDrag]         = useState(null);
  const [over, setOver]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState("");
  const [pvInt, setPvInt]       = useState(null);
  const [filterPrio, setFilterPrio] = useState("all");
  const [assigningId, setAssigningId] = useState(null);
  const [sigToast, setSigToast]     = useState("");
  const [sigForm, setSigForm]       = useState(null); // {item, email}
  const [sigSending, setSigSending] = useState(false);

  const showSigToast = (msg) => { setSigToast(msg); setTimeout(() => setSigToast(""), 3500); };

  const sendSignatureLink = async () => {
    if (!sigForm || !sigForm.email.trim()) return;
    setSigSending(true);
    try {
      const r = await fetch(`${BASE}/interventions/${sigForm.item.id}/send-signature-link`, {
        method: "POST", headers: h(),
        body: JSON.stringify({ email: sigForm.email.trim() }),
      });
      let d = {}; try { d = await r.json(); } catch {}
      if (!r.ok) {
        showSigToast("❌ " + (d.error || `Erreur ${r.status}`));
      } else if (d.emailOk === false) {
        if (d.signUrl) {
          try { await navigator.clipboard.writeText(d.signUrl); } catch {}
          showSigToast("⚠️ SMTP non configuré — lien copié dans le presse-papier");
        } else {
          showSigToast("⚠️ Email non envoyé : " + (d.emailError || "erreur SMTP"));
        }
      } else {
        showSigToast("📧 Lien envoyé à " + sigForm.email.trim());
        setSigForm(null);
      }
    } catch { showSigToast("❌ Impossible de joindre le serveur"); }
    setSigSending(false);
  };

  const modeRef = useRef(mode);
  useEffect(()=>{modeRef.current=mode; localStorage.setItem("pipeline_mode",mode);},[mode]);

  const isSav  = mode==="sav";
  const cols   = isSav ? COLS_SAV : COLS_INT;
  const getCol = isSav ? getColSav : getColInt;
  const raw    = isSav ? tickets : interventions;

  const items = useMemo(()=>{
    let list = raw.map(i=>({...i, _col:getCol(i)}));
    if(search){
      const s = search.toLowerCase();
      list = list.filter(i=>(i.objet||i.titre||"").toLowerCase().includes(s)||(i.client_nom||"").toLowerCase().includes(s)||(i.reference||"").toLowerCase().includes(s));
    }
    if(filterPrio!=="all"){
      const ALIASES = {urgente:["urgente","urgent","critique"], haute:["haute","high"]};
      const valid = ALIASES[filterPrio]||[filterPrio];
      list = list.filter(i=>valid.includes(i.priorite));
    }
    return list;
  },[raw, search, filterPrio, mode]);

  const onDrop = async(e, colId)=>{
    e.preventDefault(); e.stopPropagation();
    const m = localStorage.getItem("pipeline_mode")||"sav";
    const dragItem = JSON.parse(e.dataTransfer.getData("text/plain")||"null");
    if(!dragItem) return setDrag(null), setOver(null);
    const colSet = m==="sav" ? COLS_SAV : COLS_INT;
    const realStatut = colSet.find(x=>x.id===colId)?.real || colId;
    setSaving(true);
    const url = m==="sav" ? `${BASE}/tickets/${dragItem.id}` : `${BASE}/interventions/${dragItem.id}/statut`;
    const resp = await fetch(url, {method:"PATCH", headers:h(), body:JSON.stringify({statut:realStatut})});
    const result = await resp.json().catch(()=>({}));
    setSaving(false); setDrag(null); setOver(null);
    if(resp.ok) onUpdated&&onUpdated();
    else alert("Erreur: "+resp.status+" — "+(result.error||"?"));
  };

  const TYPE_LABELS = {installation:"Installation",maintenance:"Maintenance",depannage:"Dépannage",diagnostic:"Diagnostic","mise en service":"Mise en service",sav:"SAV"};
  const TYPE_COLORS = {installation:"#8b5cf6",maintenance:"#3b82f6",depannage:"#f97316",diagnostic:"#06b6d4","mise en service":"#10b981",sav:"#ef4444"};

  const Card = ({item}) => {
    const late    = isLate(item, mode);
    const hot     = PRIO_HOT.includes(item.priorite);
    const age     = fmtAge(item.created_at||item.date_planifiee||item.date_intervention);
    const cName   = item.client_nom||(clients.find(c=>c.id===item.client_id)?.raison_sociale)||"";
    const techName= item.technicien_nom||"";
    const typeKey = (item.type_intervention||"").toLowerCase();
    const isBlocked   = item._col==="a_replanifier";
    const isNonResolu = item._col==="non_resolu";
    const borderCol   = isBlocked?"#f97316":isNonResolu?"#ef4444":hot?"#ef4444":late?"#f59e0b":T.accent+"40";
    const cardBg      = isBlocked?"#fff7ed":isNonResolu?"#fff5f5":"#fff";

    return (
      <div draggable
        onDragStart={e=>{setDrag(item);e.dataTransfer.effectAllowed="move";e.dataTransfer.setData("text/plain",JSON.stringify({id:item.id,_col:item._col}));}}
        onDragEnd={()=>setDrag(null)}
        onClick={e=>{if(e.target.closest("[data-nd]"))return; isSav?onSelectTicket&&onSelectTicket(item):onSelectInt&&onSelectInt(item);}}
        style={{background:cardBg,borderRadius:10,padding:"11px 13px",cursor:"grab",marginBottom:7,
          border:`1px solid ${isBlocked?"#f9731640":isNonResolu?"#ef444430":"rgba(0,0,0,0.07)"}`,
          borderLeft:`3px solid ${borderCol}`,
          opacity:drag?.id===item.id?0.4:1,transition:"all 0.12s",userSelect:"none"}}
        onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 4px 14px rgba(0,0,0,0.09)";e.currentTarget.style.transform="translateY(-1px)";}}
        onMouseLeave={e=>{e.currentTarget.style.boxShadow="";e.currentTarget.style.transform="";}}>

        {/* Badge bloqué */}
        {isBlocked&&(
          <div style={{marginBottom:5}}>
            <span style={{fontSize:9,fontWeight:800,color:"#f97316",background:"#f9731620",padding:"2px 7px",borderRadius:99,animation:"pipeline-blink 1.4s infinite",letterSpacing:"0.3px"}}>
              ● BLOQUÉ — RAPPELER
            </span>
          </div>
        )}

        {/* Titre */}
        <div style={{fontSize:12,fontWeight:700,color:"#0f1428",lineHeight:1.35,marginBottom:cName?4:7}}>
          {item.objet||item.titre||"Sans titre"}
        </div>

        {/* Client */}
        {cName&&<div style={{fontSize:11,color:"#64748b",marginBottom:5}}>👤 {cName}</div>}

        {/* Footer */}
        <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
          {/* Badge type d'intervention */}
          {!isSav&&typeKey&&TYPE_LABELS[typeKey]&&(
            <span style={{fontSize:9,fontWeight:700,color:TYPE_COLORS[typeKey]||"#64748b",background:(TYPE_COLORS[typeKey]||"#64748b")+"18",padding:"1px 6px",borderRadius:99,textTransform:"uppercase",letterSpacing:"0.2px"}}>
              {TYPE_LABELS[typeKey]}
            </span>
          )}
          {/* Ref */}
          <span style={{fontSize:10,color:"#94a3b8",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {item.reference||""}
          </span>
          {/* Badges prio/retard */}
          {late&&<span style={{fontSize:9,fontWeight:700,color:"#f59e0b",background:"#f59e0b15",padding:"1px 5px",borderRadius:99}}>⏱ {age}</span>}
          {hot &&<span style={{fontSize:9,fontWeight:700,color:"#ef4444",background:"#ef444415",padding:"1px 5px",borderRadius:99}}>{item.priorite}</span>}
          {/* Assignation */}
          <div style={{position:"relative"}} data-nd="1">
            {assigningId===item.id?(
              <select autoFocus onBlur={()=>setAssigningId(null)}
                onChange={e=>{if(e.target.value){assignTech(item,e.target.value);setAssigningId(null);}}}
                style={{fontSize:10,borderRadius:6,border:"1px solid #cbd5e1",padding:"2px 4px",background:"#fff",cursor:"pointer",maxWidth:90,outline:"none"}}
                onClick={e=>e.stopPropagation()}>
                <option value="">— Tech —</option>
                {techniciens.map(t=><option key={t.id} value={t.id}>{t.prenom} {t.nom?.charAt(0)}.</option>)}
              </select>
            ):(
              <button onClick={e=>{e.stopPropagation();setAssigningId(item.id);}}
                style={{background:"none",border:"1px solid #e2e8f0",borderRadius:6,color:techName?"#5b6ef8":"#94a3b8",cursor:"pointer",fontSize:10,padding:"2px 6px",lineHeight:1.4}}
                title={techName?"Réassigner: "+techName:"Assigner un technicien"}>
                👷 {techName?techName.split(" ")[0]:"Assigner"}
              </button>
            )}
          </div>
          {!isSav && (
            <button data-nd="1" onClick={e=>{e.stopPropagation();setPvInt(item);}}
              style={{background:"none",border:"1px solid #d8b4fe",borderRadius:6,color:"#7c3aed",cursor:"pointer",fontSize:10,padding:"2px 6px",lineHeight:1.4,fontWeight:700}}
              title="PV d'intervention">
              📋 PV
            </button>
          )}
          {!isSav && (
            <button data-nd="1"
              onClick={e=>{e.stopPropagation();setSigForm({item, email:item.client_email||""});}}
              style={{background:"none",border:"1px solid #7dd3fc",borderRadius:6,color:"#0369a1",cursor:"pointer",fontSize:10,padding:"2px 6px",lineHeight:1.4,fontWeight:700}}
              title="Envoyer le lien de signature au client">
              📧
            </button>
          )}
          <button data-nd="1" onClick={e=>{e.stopPropagation();deleteItem(item);}}
            style={{background:"none",border:"none",color:"#cbd5e1",cursor:"pointer",fontSize:12,padding:"2px 4px",borderRadius:4,lineHeight:1}}
            onMouseEnter={e=>e.currentTarget.style.color="#ef4444"}
            onMouseLeave={e=>e.currentTarget.style.color="#cbd5e1"}>✕</button>
        </div>
      </div>
    );
  };

  const blockedItems = items.filter(i=>i._col==="a_replanifier");
  const counts = { sav: tickets.length, int: interventions.length };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 92px)",gap:10}}>
      <style>{BLINK_CSS}</style>

      {/* Bannière bloqués */}
      {blockedItems.length>0&&(
        <div style={{background:"linear-gradient(90deg,#fff7ed,#fef3c7)",border:"2px solid #f97316",borderRadius:12,padding:"10px 16px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <span style={{fontSize:16,animation:"pipeline-shake 0.6s ease"}}>🔶</span>
          <div style={{flex:1}}>
            <span style={{fontWeight:800,fontSize:13,color:"#c2410c"}}>
              {blockedItems.length} dossier{blockedItems.length>1?"s":""} à re-planifier
            </span>
            <span style={{fontSize:12,color:"#92400e",marginLeft:8}}>
              — {blockedItems.map(i=>i.client_nom||i.titre||"?").slice(0,3).join(", ")}{blockedItems.length>3?" …":""}
            </span>
          </div>
          <span style={{fontSize:11,color:"#92400e",background:"#fed7aa",padding:"3px 10px",borderRadius:99,fontWeight:700,animation:"pipeline-blink 2s infinite"}}>● ACTION REQUISE</span>
        </div>
      )}

      {/* Bannière SSE échecs temps réel */}
      {echecAlerts.length>0&&(
        <div style={{background:"#fef2f2",border:"2px solid #ef4444",borderRadius:12,padding:"10px 16px",display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
          {echecAlerts.map((a,idx)=>(
            <div key={idx} style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:14}}>🚨</span>
              <span style={{fontSize:12,fontWeight:700,color:"#991b1b"}}>Échec chez {a.client_nom||"?"}</span>
              <span style={{fontSize:11,color:"#b91c1c"}}>— {a.tech_nom} : {a.motif||"Motif non précisé"}</span>
              <span style={{fontSize:10,color:"#ef4444",marginLeft:"auto",background:"#fecaca",padding:"2px 8px",borderRadius:99,fontWeight:700,animation:"pipeline-blink 1.2s infinite"}}>
                Placé en re-planification
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Barre de contrôle ── */}
      <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>

        {/* Toggle SAV / Interventions */}
        <div style={{display:"flex",background:T.card,border:`1px solid ${T.border}`,borderRadius:9,padding:3,gap:2}}>
          {[
            {k:"sav", lbl:"SAV",          count:counts.sav},
            {k:"int", lbl:"Interventions", count:counts.int},
          ].map(({k,lbl,count})=>(
            <button key={k}
              onClick={()=>{localStorage.setItem("pipeline_mode",k);setMode(k);setSearch("");setFilterPrio("all");setAssigningId(null);}}
              style={{padding:"6px 18px",borderRadius:7,border:"none",
                background:mode===k?T.accent:"transparent",
                color:mode===k?"#fff":T.muted,
                fontSize:12,fontWeight:600,cursor:"pointer",transition:"all 0.15s",whiteSpace:"nowrap"}}>
              {lbl} <span style={{fontSize:11,opacity:0.7}}>({count})</span>
            </button>
          ))}
        </div>

        {/* Recherche */}
        <div style={{flex:1,minWidth:160,position:"relative"}}>
          <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:12,color:T.muted}}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Titre, client, référence…"
            style={{width:"100%",padding:"7px 10px 7px 28px",borderRadius:9,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:12,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}
            onFocus={e=>e.target.style.borderColor=T.accent}
            onBlur={e=>e.target.style.borderColor=T.border}/>
        </div>

        {/* Filtre priorité */}
        <div style={{display:"flex",gap:4}}>
          {[["all","Toutes"],["urgente","🔴 Urgent"],["haute","🟡 Haute"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilterPrio(v)}
              style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${filterPrio===v?T.accent:T.border}`,
                background:filterPrio===v?T.accentDim:"transparent",
                color:filterPrio===v?T.accent:T.muted,
                fontSize:11,fontWeight:filterPrio===v?700:500,cursor:"pointer"}}>
              {l}
            </button>
          ))}
        </div>

        {/* Bouton nouveau */}
        <button onClick={()=>isSav?onNewTicket&&onNewTicket():onNewInt&&onNewInt()}
          style={{padding:"7px 16px",borderRadius:9,background:T.gradient||T.accent,border:"none",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(67,97,238,0.25)"}}>
          + {isSav?"Ticket":"Intervention"}
        </button>

        {saving&&<span style={{fontSize:11,color:T.muted}}>💾…</span>}
      </div>

      {/* ── Kanban 5 colonnes ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,minmax(170px,1fr))",gap:10,flex:1,minHeight:0,overflowX:"auto"}}>
        {cols.map(col=>{
          const colItems = items.filter(i=>i._col===col.id).sort((a,b)=>{
            const pa=PRIO_HOT.includes(a.priorite)?0:1, pb=PRIO_HOT.includes(b.priorite)?0:1;
            return pa-pb||new Date(b.created_at||b.date_planifiee||0)-new Date(a.created_at||a.date_planifiee||0);
          });
          const isOver    = over===col.id && drag && drag._col!==col.id;
          const isAlert   = col.id==="a_replanifier";
          const isFailure = col.id==="non_resolu";
          return (
            <div key={col.id}
              onDragOver={e=>{e.preventDefault();e.stopPropagation();setOver(col.id);}}
              onDragLeave={e=>{if(!e.currentTarget.contains(e.relatedTarget))setOver(null);}}
              onDrop={e=>onDrop(e,col.id)}
              style={{background:isOver?`${col.color}08`:isAlert?"#fffbf5":isFailure?"#fff5f5":"#f8fafc",
                borderRadius:12,display:"flex",flexDirection:"column",overflow:"hidden",
                border:`2px solid ${isOver?col.color:isAlert&&colItems.length?"#f9731660":isFailure&&colItems.length?"#ef444450":T.border}`,
                transition:"border-color 0.15s,background 0.15s"}}>

              <div style={{padding:"10px 12px",
                background:isAlert?"#fff7ed":isFailure?"#fff0f0":"#fff",
                borderBottom:`3px solid ${col.color}`,flexShrink:0,
                display:"flex",alignItems:"center",gap:7}}>
                {isAlert  &&<span style={{fontSize:12,animation:"pipeline-blink 1.8s infinite"}}>🔶</span>}
                {isFailure&&<span style={{fontSize:12}}>⛔</span>}
                <span style={{fontSize:12,fontWeight:800,flex:1,
                  color:isAlert?"#c2410c":isFailure?"#b91c1c":"#0f1428"}}>
                  {col.lbl}
                </span>
                <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,
                  color:colItems.length?col.color:"#cbd5e1",
                  background:colItems.length?`${col.color}18`:"#f1f5f9",
                  animation:isAlert&&colItems.length?"pipeline-blink 1.4s infinite":undefined}}>
                  {colItems.length}
                </span>
              </div>

              <div style={{flex:1,overflowY:"auto",padding:8}}>
                {colItems.map(item=><Card key={item.id} item={item}/>)}
                {colItems.length===0&&!isOver&&(
                  <div style={{textAlign:"center",padding:"28px 8px",color:"#d1d5db",fontSize:11}}>Vide</div>
                )}
                {isOver&&(
                  <div style={{border:`2px dashed ${col.color}`,borderRadius:10,padding:16,textAlign:"center",color:col.color,fontSize:12,fontWeight:600,margin:4}}>
                    Déposer ici
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {sigToast && (
        <div style={{position:"fixed",top:24,right:24,zIndex:9999,background:"#1e293b",color:"#fff",padding:"11px 20px",borderRadius:12,fontSize:13,fontWeight:600,boxShadow:"0 8px 24px #00000044",pointerEvents:"none"}}>
          {sigToast}
        </div>
      )}

      {sigForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.35)",backdropFilter:"blur(4px)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:400,boxShadow:"0 20px 60px #00000030"}}>
            <div style={{fontSize:16,fontWeight:800,color:"#0f172a",marginBottom:4}}>📧 Envoyer le lien de signature</div>
            <div style={{fontSize:12,color:"#64748b",marginBottom:18}}>
              {sigForm.item.client_nom||sigForm.item.titre} — saisir l'email du destinataire
            </div>
            <input
              type="email"
              value={sigForm.email}
              onChange={e => setSigForm(f => ({...f, email: e.target.value}))}
              placeholder="email@client.fr"
              autoFocus
              onKeyDown={e => e.key==="Enter" && sendSignatureLink()}
              style={{width:"100%",padding:"10px 13px",borderRadius:9,border:"1.5px solid #7dd3fc",background:"#f0f9ff",color:"#0f172a",fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:16}}
            />
            <div style={{display:"flex",gap:10}}>
              <button
                onClick={sendSignatureLink}
                disabled={sigSending || !sigForm.email.trim()}
                style={{flex:1,padding:"11px",borderRadius:9,background:"#0369a1",border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:sigSending||!sigForm.email.trim()?"not-allowed":"pointer",opacity:sigSending||!sigForm.email.trim()?0.7:1}}>
                {sigSending ? "Envoi…" : "📨 Envoyer"}
              </button>
              <button
                onClick={() => setSigForm(null)}
                style={{padding:"11px 18px",borderRadius:9,background:"transparent",border:"1px solid #e2e8f0",color:"#64748b",fontSize:14,cursor:"pointer"}}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {pvInt && (
        <ModalPV T={T} intervention={pvInt} onClose={() => setPvInt(null)} onSaved={() => { setPvInt(null); onUpdated?.(); }} />
      )}
    </div>
  );
}
